/**
 * Payment processor configuration for TagadaPay SDK
 * These are public API keys - safe to embed in client-side code
 */

export interface PaymentConfig {
  basisTheory: {
    publicApiKey: string;
    environment: 'sandbox' | 'production';
  };
}

/**
 * Payment configurations for different environments
 */
export const PAYMENT_CONFIGS: Record<string, PaymentConfig> = {
  production: {
    basisTheory: {
      // TODO: Replace with actual production BasisTheory public API key
      publicApiKey: 'key_prod_us_pub_PNMB2AiaECJ463K6QAPNU6',
      environment: 'production',
    },
  },
  development: {
    basisTheory: {
      // Embedded test API key for development
      publicApiKey: 'key_test_us_pub_VExdfbFQARn821iqP8zNaq',
      environment: 'sandbox',
    },
  },
  local: {
    basisTheory: {
      // Embedded test API key for local development
      publicApiKey: 'key_test_us_pub_VExdfbFQARn821iqP8zNaq',
      environment: 'sandbox',
    },
  },
  // Default fallback configuration
  default: {
    basisTheory: {
      // Embedded test API key as fallback
      publicApiKey: 'key_test_us_pub_VExdfbFQARn821iqP8zNaq',
      environment: 'sandbox',
    },
  },
};

/**
 * Get payment configuration based on environment
 */
export function getPaymentConfig(environment = 'local'): PaymentConfig {
  return PAYMENT_CONFIGS[environment] || PAYMENT_CONFIGS.default || PAYMENT_CONFIGS.local;
}

import { getBasisTheoryKeys } from '../../config/basisTheory';

/**
 * Determine if the current hostname should use production BasisTheory.
 * 
 * BT key selection is based ONLY on hostname — never on tagadaClientEnv overrides.
 * tagadaClientEnv controls which API server the SDK talks to, but BasisTheory
 * tokens must always match the backend's BT tenant.
 * 
 * Production BT: *.tagada.io, *.tagadapay.com, custom domains (e.g. checkout.myboost.club)
 * Test BT:       *.tagada.dev, *.tagadapay.dev, localhost, vercel.app, etc.
 */
export function isProductionBasisTheory(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;

  // Dev backend — uses test BT
  if (hostname.includes('app-dev.tagadapay.com') || hostname.includes('cdn-dev.tagadapay.com')) {
    return false;
  }

  // Production and preprod share the same production BT tenant
  if (hostname.includes('tagada.io') || hostname.includes('tagadapay.com')) return true;

  // Dev/staging domains use test BT
  if (hostname.includes('tagada.dev') || hostname.includes('tagadapay.dev')) return false;

  // Local development — test BT
  if (
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.includes('.local') ||
    hostname === '' ||
    hostname === '0.0.0.0' ||
    hostname.includes('ngrok') ||
    hostname.includes('.loclx.io') ||
    hostname.includes('vercel.app') ||
    hostname.includes('netlify.app')
  ) {
    return false;
  }

  // Custom checkout domains (e.g. checkout.myboost.club) use production BT
  // to match the production backend's BT tenant
  return true;
}

/**
 * Get BasisTheory API key based on hostname detection.
 * Not affected by tagadaClientEnv overrides.
 */
export function getBasisTheoryApiKey(): string {
  const keys = getBasisTheoryKeys(isProductionBasisTheory());
  return keys.apiKey;
}

/**
 * Get BasisTheory tenant ID based on hostname detection.
 * Not affected by tagadaClientEnv overrides.
 */
export function getBasisTheoryTenantId(): string {
  const keys = getBasisTheoryKeys(isProductionBasisTheory());
  return keys.tenantId;
}
